/**
 * ریاضیات معاملات اهرمی — سمت مرورگر
 * =====================================
 * این فایل **آینه‌ی دقیقِ** `backend/app/services/leverage.py` است.
 *
 * چرا دوباره‌نویسی، به‌جای پرسیدن از سرور؟
 * کاربر پیش از فشردن دکمه باید بداند «قیمت لیکویید کجاست». اگر برای هر
 * جابه‌جاییِ نوار اهرم یک درخواست می‌رفت، هم کند می‌شد و هم روی موبایلِ
 * ضعیف عملاً بی‌استفاده. پس همان چند خط فرمول اینجا هم هست.
 *
 * خطرِ این کار روشن است: **دو نسخه که ممکن است از هم فاصله بگیرند**.
 * برای همین:
 *   ۱. هیچ عددی از این فایل مبنای پول نیست — سرور همه‌چیز را دوباره حساب
 *      می‌کند و عددِ سرور حاکم است. اینجا فقط «پیش‌نمایش» است.
 *   ۲. `frontend/tests/leverage.mjs` همان اعدادِ آزمونِ بک‌اند را می‌آزماید،
 *      پس اگر یکی عوض شود و دیگری نه، آزمون قرمز می‌شود.
 *
 * بدون وابستگی به مرورگر، تا مستقیم آزمون‌پذیر باشد.
 */

export const LONG = 'long'
export const SHORT = 'short'
export type Side = 'long' | 'short'

export type LevConfig = {
  enabled: boolean
  maxLeverage: number
  maintenance: number
  fee: number
  minMarginIrt: number
  maxNotionalIrt: number
  maxPositions: number
  minKyc: number
  markets: string[]
  kycOk: boolean
}

export type Position = {
  id: number
  symbol: string; base: string; quote: string
  side: Side
  leverage: number
  margin: number
  size: number
  entryPrice: number
  markPrice: number
  liqPrice: number
  tpPrice: number
  slPrice: number
  /** حاشیه نگهداریِ همین موقعیت، در لحظه باز شدن */
  maintenance: number
  notional: number
  pnl: number
  roe: number
  equity: number
  openFee: number
  closeFee: number
  payout: number
  status: 'open' | 'closed' | 'liquidated'
  closeReason: string
  closePrice: number
  createdAt: string | null
  closedAt: string | null
}

const n = (v: any): number => (Number.isFinite(Number(v)) ? Number(v) : 0)

/* ═══════════════════════════════════════════════ فرمول‌های خالص */

/** ارزش کل موقعیت = وثیقه × اهرم */
export const notionalOf = (margin: number, leverage: number): number =>
  Math.max(0, n(margin)) * Math.max(0, n(leverage))

/** اندازه موقعیت بر حسب ارز پایه */
export const sizeOf = (margin: number, leverage: number, entry: number): number =>
  n(entry) <= 0 ? 0 : notionalOf(margin, leverage) / n(entry)

/**
 * قیمتی که در آن، وثیقه به حاشیه نگهداری می‌رسد و موقعیت بسته می‌شود.
 *
 *   خرید:  ورود × (۱ − ۱/اهرم + حاشیه)
 *   فروش:  ورود × (۱ + ۱/اهرم − حاشیه)
 */
export function liqPrice(side: Side, entry: number, leverage: number, mmr: number): number {
  const e = n(entry), L = n(leverage)
  if (e <= 0 || L <= 0) return 0
  const k = 1 / L - Math.max(0, n(mmr))
  const p = side === LONG ? e * (1 - k) : e * (1 + k)
  return Math.max(0, p)
}

/** سود یا زیان، به ارز مظنه */
export function pnlOf(side: Side, entry: number, exit: number, size: number): number {
  const e = n(entry), s = n(size)
  if (e <= 0 || s <= 0) return 0
  const d = side === LONG ? n(exit) - e : e - n(exit)
  return d * s
}

/**
 * آنچه به کاربر برمی‌گردد. کفِ صفر عمدی است: در وثیقه ایزوله، زیان از
 * وثیقه بیشتر نمی‌شود و موجودی هرگز منفی نمی‌شود.
 */
export const payoutOf = (margin: number, pnl: number, closeFee: number): number =>
  Math.max(0, n(margin) + n(pnl) - Math.max(0, n(closeFee)))

export const equityOf = (margin: number, pnl: number): number => n(margin) + n(pnl)

/** بازده نسبت به وثیقه — عددی که کاربر واقعاً حس می‌کند */
export const roeOf = (margin: number, pnl: number): number =>
  n(margin) > 0 ? (n(pnl) / n(margin)) * 100 : 0

export function isLiquidated(side: Side, mark: number, liq: number): boolean {
  if (n(mark) <= 0 || n(liq) <= 0) return false
  return side === LONG ? n(mark) <= n(liq) : n(mark) >= n(liq)
}

/** بالاترین اهرمی که با این حاشیه نگهداری معنا دارد */
export const maxSafeLeverage = (mmr: number): number => (n(mmr) > 0 ? 1 / n(mmr) : 1000)

/** کارمزد باز یا بستن، روی ارزش کل */
export const feeOf = (notional: number, fee: number): number =>
  Math.max(0, n(notional)) * Math.max(0, n(fee))

/**
 * قیمتی که در آن سود/زیان صفر می‌شود — یعنی حرکت لازم برای پوشاندن
 * کارمزدِ باز و بسته کردن. کاربر معمولاً از دیدنش جا می‌خورد، و دقیقاً
 * به همین دلیل نشانش می‌دهیم.
 */
export function breakEven(side: Side, entry: number, fee: number): number {
  const e = n(entry)
  if (e <= 0) return 0
  const f = Math.max(0, n(fee))
  // کارمزدِ رفت روی ورود و کارمزدِ برگشت روی خروج است؛ با تقریبِ
  // «خروج ≈ ورود» به ضریب ساده‌ی (۱ ± ۲f) می‌رسیم.
  return side === LONG ? e * (1 + 2 * f) : e * (1 - 2 * f)
}

/* ═══════════════════════════════════════════════ پیش‌نمایش سفارش */

export type Preview = {
  notional: number
  size: number
  liq: number
  openFee: number
  closeFee: number
  breakEven: number
  /** فاصله تا لیکویید، به درصد قیمت فعلی */
  liqDistancePct: number
  total: number
}

export function previewOpen(
  cfg: Pick<LevConfig, 'maintenance' | 'fee'>,
  side: Side, entry: number, leverage: number, margin: number,
): Preview {
  const notional = notionalOf(margin, leverage)
  const size = sizeOf(margin, leverage, entry)
  const liq = liqPrice(side, entry, leverage, cfg.maintenance)
  const openFee = feeOf(notional, cfg.fee)
  const e = n(entry)
  return {
    notional, size, liq, openFee,
    closeFee: openFee,
    breakEven: breakEven(side, entry, cfg.fee),
    liqDistancePct: e > 0 && liq > 0 ? (Math.abs(e - liq) / e) * 100 : 0,
    total: n(margin) + openFee,
  }
}

/**
 * همان اعتبارسنجی‌هایی که سرور انجام می‌دهد و مرورگر هم می‌تواند بداند.
 * خروجی: متن خطا، یا رشته خالی اگر مشکلی نیست.
 *
 * این جایگزینِ اعتبارسنجی سرور **نیست** — فقط جلوی رفت‌وبرگشت بیهوده را
 * می‌گیرد و خطا را کنار همان فیلد نشان می‌دهد.
 */
export function validateOpen(
  cfg: LevConfig, symbol: string, side: string,
  leverage: number, margin: number,
  opts: { balance?: number; notionalIrt?: number; openPositions?: number } = {},
): string {
  if (!cfg.enabled) return 'معاملات اهرمی فعال نیست'
  if (side !== LONG && side !== SHORT) return 'جهت موقعیت نامعتبر است'
  if (cfg.markets.length && !cfg.markets.includes(symbol.toUpperCase()))
    return 'این بازار برای معامله اهرمی مجاز نیست'
  if (!cfg.kycOk) return `برای معامله اهرمی، احراز هویت سطح ${cfg.minKyc} لازم است`
  if (!(n(leverage) >= 1)) return 'اهرم باید دست‌کم ۱ باشد'
  if (n(leverage) > cfg.maxLeverage + 1e-9)
    return `بیشترین اهرم مجاز ${cfg.maxLeverage} برابر است`
  if (!(n(margin) > 0)) return 'مبلغ وثیقه را وارد کنید'

  const irt = n(opts.notionalIrt)
  if (cfg.minMarginIrt > 0 && irt > 0) {
    const marginIrt = irt / Math.max(1, n(leverage))
    if (marginIrt < cfg.minMarginIrt - 1e-6) return 'وثیقه کمتر از حداقل مجاز است'
  }
  if (cfg.maxNotionalIrt > 0 && irt > cfg.maxNotionalIrt)
    return 'ارزش موقعیت از سقف مجاز بیشتر است'
  if (cfg.maxPositions > 0 && n(opts.openPositions) >= cfg.maxPositions)
    return `بیشتر از ${cfg.maxPositions} موقعیت باز نمی‌شود`

  const need = n(margin) + feeOf(notionalOf(margin, leverage), cfg.fee)
  if (opts.balance !== undefined && need > n(opts.balance) + 1e-9)
    return 'موجودی کافی نیست'
  return ''
}

/**
 * اعتبارسنجی حد سود و حد ضرر — آینه‌ی `check_targets` در بک‌اند.
 *
 * دو قانون، و دومی مهم‌تر است:
 *
 * **سمتِ درست.** حد سودِ پایین‌تر از قیمت فعلی برای یک موقعیت خرید یعنی
 * «همین حالا ببند»؛ کاربر برای هیچ، دو بار کارمزد می‌دهد.
 *
 * **آن‌سوی قیمت لیکویید نباشد.** موتور تسویه اول لیکویید را می‌سنجد و بعد
 * حد ضرر را. پس حد ضررِ پشتِ قیمت لیکویید **هرگز اجرا نمی‌شود** — کاربر
 * خیال می‌کند زیانش را محدود کرده و در عمل کل وثیقه را می‌بازد.
 *
 * خروجی: متن خطا، یا رشته خالی.
 */
export function checkTargets(
  side: Side, mark: number, liq: number, tp: number, sl: number,
): string {
  const m = n(mark), a = Math.max(0, n(tp)), b = Math.max(0, n(sl)), l = n(liq)
  if (m <= 0) return ''
  if (side === LONG) {
    if (a && a <= m) return 'حد سود باید بالاتر از قیمت فعلی باشد'
    if (b && b >= m) return 'حد ضرر باید پایین‌تر از قیمت فعلی باشد'
    if (b && l > 0 && b <= l) return 'حد ضرر باید بالاتر از قیمت لیکویید باشد، وگرنه هرگز اجرا نمی‌شود'
  } else {
    if (a && a >= m) return 'حد سود باید پایین‌تر از قیمت فعلی باشد'
    if (b && b <= m) return 'حد ضرر باید بالاتر از قیمت فعلی باشد'
    if (b && l > 0 && b >= l) return 'حد ضرر باید پایین‌تر از قیمت لیکویید باشد، وگرنه هرگز اجرا نمی‌شود'
  }
  return ''
}

/** پله‌های آماده نوار اهرم — فقط آن‌هایی که زیر سقف تنظیمات هستند */
export function leverageSteps(max: number): number[] {
  return [2, 3, 5, 10, 20, 25, 50, 100].filter((v) => v <= n(max) + 1e-9)
}

export const sideLabel = (s: string) => (s === LONG ? 'خرید (Long)' : 'فروش (Short)')

export const closeReasonLabel = (r: string): string => ({
  user: 'بستن دستی',
  liquidation: 'لیکویید شد',
  take_profit: 'حد سود',
  stop_loss: 'حد ضرر',
}[r] || '—')
